import { ActionIcon, Box, Group, Progress, Text, Tooltip } from "@mantine/core"
import { modals } from "@mantine/modals"
import { showNotification } from "@mantine/notifications"
import { IconEdit, IconTrash } from "@tabler/icons-react"
import {
  createMRTColumnHelper,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table"
import { Link } from "react-router-dom"

import { AlertsResponses } from "@costory/types/endpoints/alerts"
import { AlertPeriod } from "@costory/types/prisma-client"

import { ALL_VALUES } from "@costory/shared/const"

import { HoverHighlightCell } from "@costory/front/components/common/NavigateNewTab"
import { useDeleteAlertMutation } from "@costory/front/queries/alerts"
import { getDefaultTableOptions } from "@costory/front/utils/table"

export default function AlertTable({
  alertData,
}: {
  alertData: AlertsResponses.DetailedAlert[]
}) {
  const { mutateAsync: deleteAlert } = useDeleteAlertMutation()

  const columnHelper = createMRTColumnHelper<AlertsResponses.DetailedAlert>()

  const onConfirmDeleteAlert = async (id: string) => {
    try {
      await deleteAlert(id)
      showNotification({ title: "Success", message: "Alert deleted" })
    } catch {
      showNotification({
        title: "Error",
        message: "Failed to delete alert",
        color: "red",
      })
    }
  }

  // Define the columns for the table
  const columns = [
    columnHelper.accessor("name", {
      header: "Name",
      Cell: ({ row, cell }) => {
        const alertId = row.original.id

        return (
          <HoverHighlightCell
            value={cell.getValue()}
            id={`${alertId}/edit`}
            basePath="alerts/"
          />
        )
      },
    }),
    columnHelper.accessor((row) => row.computationResults[0]?.alertStatus, {
      header: "Status",
      size: 150,
      enableColumnActions: true,
      Cell: ({ row: { original: alert } }) => {
        const statusColor =
          alert.currentStatus === null
            ? "textMuted"
            : alert.currentStatus.toLowerCase()

        return (
          <Group justify="space-between" wrap="nowrap">
            {alert.period !== AlertPeriod.DAILY && (
              <>
                <Progress.Root
                  h={20}
                  w={70}
                  bg={`${statusColor}.2`}
                  styles={{ root: { borderRadius: 8 } }}
                >
                  <Progress.Section
                    value={alert.progress}
                    color={`${statusColor}.5`}
                  />
                </Progress.Root>
                <Text>{`${alert.progress.toFixed(0)}%`}</Text>
              </>
            )}
          </Group>
        )
      },
    }),
    columnHelper.display({
      header: "Last status",
      size: 200,
      enableColumnActions: true,
      Cell: ({ row: { original: alert } }) => {
        const barWidth = 150

        return (
          <Group h={32} gap={2} w={barWidth}>
            {alert.lastTriggers.map((trigger, index) => (
              <Tooltip label={trigger.dateLabel} key={index}>
                <Box
                  bg={
                    trigger.status !== null
                      ? `${trigger.status.toLowerCase()}.5`
                      : "lightgray"
                  }
                  style={{
                    width: 16,
                    height: "100%",
                    ...(index === 0 && { borderRadius: "4px 0 0 4px" }),
                    ...(index === alert.lastTriggers.length - 1 && {
                      borderRadius: "0px 4px 4px 0px",
                    }),
                  }}
                />
              </Tooltip>
            ))}
          </Group>
        )
      },
    }),
    columnHelper.accessor("virtualDimension.name", {
      header: "Virtual Dimension",
    }),
    columnHelper.accessor(
      (row) =>
        row.virtualDimensionValue === ALL_VALUES
          ? "All values"
          : row.virtualDimensionValue,
      {
        header: "Value",
        size: 100,
      },
    ),
    columnHelper.display({
      header: "Actions",
      enableColumnActions: false,
      Cell: ({
        row: {
          original: { id: alertId },
        },
      }) => (
        <Group>
          <ActionIcon
            component={Link}
            to={`${alertId}/edit`}
            bg="primary"
            color="textOnError"
          >
            <IconEdit />
          </ActionIcon>
          <ActionIcon
            onClick={() => {
              modals.openConfirmModal({
                title: "Are you sure?",
                confirmProps: { color: "red" },
                labels: { cancel: "Cancel", confirm: "Delete" },
                onConfirm: () => onConfirmDeleteAlert(alertId),
              })
            }}
            bg="red"
            color="textOnError"
          >
            <IconTrash />
          </ActionIcon>
        </Group>
      ),
    }),
  ]

  const table = useMantineReactTable<AlertsResponses.DetailedAlert>({
    columns,
    data: alertData,
    ...getDefaultTableOptions(),
  })

  return <MantineReactTable table={table} />
}
